/* =====================================================================
 * PricingPage — 플랜 / 가격 페이지 (공개 접근 가능)
 *
 * 첨부 이미지 (Cursor Pricing) 참고 UI, 앱 theme 적용
 * ===================================================================== */

import React, { useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelBillingSubscription,
  changeBillingSubscriptionPlan,
  createBillingCheckout,
  getBillingCatalog,
  getBillingStatus,
  hasAuthToken,
  trialUpgradeCheckout,
  type BillingCatalog,
  type BillingStatus,
} from '@auw/api-client';
import { formatCurrencyAmount, resolvePlanPricing } from '@auw/api-client';
import { openPaddleCheckout } from '@auw/workspace-utils';
import { modeToPath } from '@auw/workspace-utils';
import { useUserStore } from '@auw/stores';
import { useChatStore } from '@auw/stores';
import { queryKeys } from '@auw/workspace-utils';
import {
  PLANS,
  INDIVIDUAL_PLANS,
  type PlanType,
  type BillingCycle,
  type PlanInfo,
  type PlanFeature,
} from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const SUCCESS_REDIRECT_DELAY_MS = 1200;

export const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation('widget');
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [loading, setLoading] = useState<PlanType | null>(null);
  const overlayOpenRef = useRef(false);

  const user = useUserStore((s) => s.user);
  const loadUser = useUserStore((s) => s.loadUser);
  const updatePlan = useUserStore((s) => s.updatePlan);
  const addToast = useChatStore((s) => s.addToast);
  const isAuthed = !!user;
  const currentPlan = user?.plan ?? null;
  const hasBillingSession = isAuthed || hasAuthToken();
  const showGuestTrialMarketing = !hasBillingSession;

  const billingQuery = useQuery<BillingStatus>({
    queryKey: queryKeys.billing.status(),
    queryFn: getBillingStatus,
    enabled: hasBillingSession,
    refetchOnWindowFocus: true,
  });
  const catalogQuery = useQuery<BillingCatalog>({
    queryKey: queryKeys.billing.catalog(),
    queryFn: getBillingCatalog,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const billingStatus = hasBillingSession ? (billingQuery.data ?? null) : null;
  const billingCatalog = catalogQuery.data ?? null;
  const normalizedCurrentBillingStatus = String(billingStatus?.billing_status || '').trim().toLowerCase();
  const hasCurrentManagedSubscription = Boolean(
    billingStatus?.subscription_id
    && ['active', 'trialing', 'past_due', 'paused', 'canceling'].includes(normalizedCurrentBillingStatus),
  );
  const currentBillingCycle = String(billingStatus?.billing_cycle || '').trim().toLowerCase();
  const effectiveCurrentPlan = (billingStatus?.current_plan ?? currentPlan) as PlanType | null;
  const proTrialAvailable = showGuestTrialMarketing
    ? true
    : billingQuery.isError
      ? false
      : (billingStatus ? Boolean(billingStatus.can_start_pro_trial) : null);

  /* Phase 11-3a: `/agents` 를 `modeToPath('agent')` 로 흡수해 on-prem
   * 테넌트가 agent 모드를 비활성화해도 올바른 랜딩으로 이동하도록 함.
   * Pricing 자체가 B2C 결제 플로우에 한정되지만 tenant 일관성을 위해
   * 명시적으로 경유. */
  const workspaceLandingPath = modeToPath('agent');

  const handleBack = () => {
    nav(workspaceLandingPath);
  };

  const refreshBillingStatus = useCallback(async (): Promise<BillingStatus | null> => {
    try {
      return await queryClient.fetchQuery({
        queryKey: queryKeys.billing.status(),
        queryFn: getBillingStatus,
        staleTime: 0,
      });
    } catch {
      return null;
    }
  }, [queryClient]);

  const launchCheckoutOverlay = useCallback(
    async (transactionId: string) => {
      overlayOpenRef.current = true;
      await openPaddleCheckout(transactionId, {
        onCompleted: () => {
          addToast({
            type: 'success',
            title: t('pricing.paymentComplete'),
            description: t('pricing.redirectingSoon'),
          });
          loadUser();
          queryClient.invalidateQueries({ queryKey: queryKeys.billing.status() });
          window.setTimeout(() => window.location.assign(workspaceLandingPath), SUCCESS_REDIRECT_DELAY_MS);
        },
        onClosed: () => {
          overlayOpenRef.current = false;
          setLoading(null);
        },
      });
    },
    [addToast, loadUser, queryClient, workspaceLandingPath],
  );

  const handleGetPlan = async (plan: PlanType) => {
    if (plan === 'enterprise') {
      nav('/contact-sales');
      return;
    }

    if (!isAuthed && !hasAuthToken()) {
      nav(workspaceLandingPath);
      return;
    }

    setLoading(plan);
    try {
      let effectiveBillingStatus = billingStatus;
      if (isAuthed) {
        const refreshedStatus = await refreshBillingStatus();
        if (refreshedStatus) effectiveBillingStatus = refreshedStatus;
      }

      const normalizedBillingStatus = String(effectiveBillingStatus?.billing_status || '').trim().toLowerCase();
      const hasManagedSubscription = Boolean(
        effectiveBillingStatus?.subscription_id
        && ['active', 'trialing', 'past_due', 'paused', 'canceling'].includes(normalizedBillingStatus),
      );
      const resolvedCurrentPlan = (effectiveBillingStatus?.current_plan ?? currentPlan) as PlanType | null;
      const resolvedCurrentCycle = String(effectiveBillingStatus?.billing_cycle || '').trim().toLowerCase();
      const isSameSelection = resolvedCurrentPlan === plan && (
        plan === 'free'
        || (hasManagedSubscription && resolvedCurrentCycle === cycle)
      );
      if (isSameSelection) return;

      if (plan === 'free') {
        if (isAuthed && hasManagedSubscription) {
          await cancelBillingSubscription(effectiveBillingStatus?.subscription_id || undefined);
          await queryClient.invalidateQueries({ queryKey: queryKeys.billing.status() });
          addToast({
            type: 'success',
            title: t('pricing.cancellationScheduled'),
            description: t('pricing.cancelDescription'),
          });
          setTimeout(() => { nav(workspaceLandingPath); }, 1000);
          return;
        }

        const ok = await updatePlan(plan);
        if (ok) {
          addToast({
            type: 'success',
            title: t('pricing.planChanged'),
            description: t('pricing.planChangedDesc', { plan: getPlanName(plan) }),
          });
          setTimeout(() => { nav(workspaceLandingPath); }, 800);
        } else {
          addToast({
            type: 'error',
            title: t('pricing.planChangeFailed'),
            description: t('pricing.tryAgainLater'),
          });
        }
        return;
      }

      if (isAuthed && hasManagedSubscription) {
        try {
          const changed = await changeBillingSubscriptionPlan({
            plan,
            cycle,
          });
          await queryClient.invalidateQueries({ queryKey: queryKeys.billing.status() });
          if (changed.effective_timing === 'immediate') {
            await loadUser();
          }
          addToast({
            type: 'success',
            title: t('pricing.subscriptionChangeRequested'),
            description: changed.effective_timing === 'immediate'
              ? t('pricing.planAppliedImmediately')
              : t('pricing.planChangeNextBilling'),
          });
          setTimeout(() => { nav(workspaceLandingPath); }, 1000);
        } catch (changeError) {
          const isConsentLock = changeError instanceof Error && (
            /consent.review/i.test(changeError.message)
            || /구독 변경이 잠시 잠겨/.test(changeError.message)
          );
          if (isConsentLock && normalizedBillingStatus === 'trialing') {
            addToast({
              type: 'info',
              title: t('pricing.planTransition'),
              description: t('pricing.trialEndingNewPlan'),
            });
            const upgrade = await trialUpgradeCheckout({ plan, cycle });
            if (upgrade.transaction_id) {
              await launchCheckoutOverlay(upgrade.transaction_id);
            } else {
              window.location.href = upgrade.checkout_url;
            }
          } else {
            throw changeError;
          }
        }
        return;
      }

      const checkout = await createBillingCheckout({
        plan,
        cycle,
      });
      if (checkout.transaction_id) {
        await launchCheckoutOverlay(checkout.transaction_id);
      } else {
        window.location.href = checkout.checkout_url;
      }
    } catch (error) {
      const title = error instanceof Error && /Change subscription plan failed/i.test(error.message)
        ? t('pricing.subscriptionChangeFailed')
        : t('pricing.checkoutFailed');
      const message = (() => {
        if (!(error instanceof Error)) return t('pricing.tryAgainLater');
        const detailMatch = error.message.match(/"detail"\s*:\s*"([^"]+)"/);
        const rawMessage = detailMatch?.[1]
          || error.message.replace(/^[A-Za-z ]+failed \(\d+\):\s*/i, '').trim()
          || t('pricing.tryAgainLater');
        const normalized = rawMessage.toLowerCase();
        if (
          normalized.includes('subscription_locked_consent_review_period')
          || normalized.includes('mandatory consent review period')
        ) {
          return t('pricing.subscriptionLocked');
        }
        return rawMessage;
      })();
      addToast({
        type: 'error',
        title,
        description: message,
      });
    } finally {
      if (!overlayOpenRef.current) setLoading(null);
    }
  };

  const formatFeatureNumber = useCallback(
    (value: number) => new Intl.NumberFormat(i18n.language).format(value),
    [i18n.language],
  );

  const getPlanName = useCallback(
    (planType: PlanType): string => t(`pricing.planNames.${planType}`),
    [t],
  );

  const getFeatureText = useCallback((feature: PlanFeature): string => {
    switch (feature.id) {
      case 'tokens':
        return t('pricing.planFeatures.tokens', { count: formatFeatureNumber(feature.value) });
      case 'storage':
        return t('pricing.planFeatures.storageGB', { gb: feature.value });
      case 'basicAgentAccess':
        return t('pricing.planFeatures.basicAgentAccess');
      case 'communitySupport':
        return t('pricing.planFeatures.communitySupport');
      case 'includesPlan':
        return t('pricing.planFeatures.includesPlan', { plan: getPlanName(feature.plan) });
      case 'teamSharedChatAndRules':
        return t('pricing.planFeatures.teamSharedChatAndRules');
      case 'centralizedTeamBilling':
        return t('pricing.planFeatures.centralizedTeamBilling');
      case 'usageAnalyticsAndReporting':
        return t('pricing.planFeatures.usageAnalyticsAndReporting');
      case 'organizationPrivacyControls':
        return t('pricing.planFeatures.organizationPrivacyControls');
      case 'roleBasedAccessControl':
        return t('pricing.planFeatures.roleBasedAccessControl');
      case 'samlOidcSso':
        return t('pricing.planFeatures.samlOidcSso');
      case 'integratedUsagePool':
        return t('pricing.planFeatures.integratedUsagePool');
      case 'invoicePoBilling':
        return t('pricing.planFeatures.invoicePoBilling');
      case 'scimSeatManagement':
        return t('pricing.planFeatures.scimSeatManagement');
      case 'fineGrainedAdminModelControls':
        return t('pricing.planFeatures.fineGrainedAdminModelControls');
      case 'prioritySupportAndAccountManagement':
        return t('pricing.planFeatures.prioritySupportAndAccountManagement');
      default:
        return '';
    }
  }, [formatFeatureNumber, getPlanName, t]);

  const getFeatureLabel = useCallback((plan: PlanInfo): string => {
    const includesPlanFeature = plan.features.find(
      (feature): feature is Extract<PlanFeature, { id: 'includesPlan' }> => feature.id === 'includesPlan',
    );

    if (!includesPlanFeature) {
      return t('pricing.featureIncludes');
    }

    return t('pricing.featureEverythingInPlanPlus', {
      plan: getPlanName(includesPlanFeature.plan),
    });
  }, [getPlanName, t]);

  const buildFeatureItems = (plan: PlanInfo): string[] => {
    const translatedFeatures = plan.features.map((feature) => getFeatureText(feature));
    const extraItems: string[] = [];

    if (plan.type !== 'enterprise' && typeof plan.teamStorageGB === 'number') {
      extraItems.push(t('pricing.teamStorageGB', { gb: plan.teamStorageGB }));
    }
    if (plan.type !== 'enterprise' && typeof plan.teamSpaceLimit === 'number') {
      extraItems.push(t('pricing.teamSpaceLimit', { limit: plan.teamSpaceLimit }));
    }
    if (extraItems.length === 0) return translatedFeatures;

    const storageIdx = plan.features.findIndex((feature) => feature.id === 'storage');
    const insertAt = storageIdx >= 0 ? storageIdx + 1 : translatedFeatures.length;
    return [
      ...translatedFeatures.slice(0, insertAt),
      ...extraItems,
      ...translatedFeatures.slice(insertAt),
    ];
  };

  return (
    <Wrap>
      {/* Top Bar */}
      <TopBar>
        <BackButton onClick={handleBack} aria-label={t('pricing.goBack')}>
          <ArrowLeftIcon />
        </BackButton>
        <TopBarTitle>{t('pricing.topBarTitle')}</TopBarTitle>
      </TopBar>

      {/* Title */}
      <PageTitle>{t('pricing.pageTitle')}</PageTitle>

      {/* Billing Toggle */}
      <ToggleWrap>
        <TogglePill>
          <ToggleOption $active={cycle === 'monthly'} onClick={() => setCycle('monthly')}>
            {t('pricing.monthly')}
          </ToggleOption>
          <ToggleOption $active={cycle === 'yearly'} onClick={() => setCycle('yearly')}>
            {t('pricing.yearly')}
          </ToggleOption>
        </TogglePill>
      </ToggleWrap>

      {/* Individual Plans */}
      <SectionLabel>{t('pricing.individualPlans')}</SectionLabel>
      <CardsRow>
        {INDIVIDUAL_PLANS.map((planKey) => {
          const plan = PLANS[planKey];
          const resolvedPricing = resolvePlanPricing(plan, billingCatalog);
          const isCurrent = effectiveCurrentPlan === planKey && (
            planKey === 'free'
            || (hasCurrentManagedSubscription && currentBillingCycle === cycle)
          );
          const isProPlan = planKey === 'pro';
          const isTrialStatusLoading = isProPlan && hasBillingSession && proTrialAvailable === null;
          const showProTrial = isProPlan && (showGuestTrialMarketing || proTrialAvailable === true);
          const showTrialUnavailable = isProPlan && hasBillingSession && proTrialAvailable === false;
          const monthlyAmount = cycle === 'yearly' && resolvedPricing.yearlyMonthlyEquivalent > 0
            ? resolvedPricing.yearlyMonthlyEquivalent
            : resolvedPricing.monthlyAmount;
          const yearlyTotal = cycle === 'yearly' ? resolvedPricing.yearlyTotal : 0;
          const monthlyPriceLabel = formatCurrencyAmount(monthlyAmount, resolvedPricing.currencyCode, i18n.language);
          const originalMonthlyPriceLabel = formatCurrencyAmount(
            resolvedPricing.monthlyAmount,
            resolvedPricing.currencyCode,
            i18n.language,
          );
          const yearlyTotalLabel = formatCurrencyAmount(yearlyTotal, resolvedPricing.currencyCode, i18n.language);
          const featureItems = buildFeatureItems(plan);

          return (
            <PlanCard key={planKey} $recommended={!!plan.recommended}>
              <CardTop>
                <CardHeader>
                  <PlanName>{getPlanName(planKey)}</PlanName>
                  {plan.recommended && <RecommendedBadge>{t('pricing.recommended')}</RecommendedBadge>}
                  {showProTrial && <TrialBadge>{t('pricing.trialBadge')}</TrialBadge>}
                </CardHeader>

                <PriceRow>
                  {plan.monthlyPrice === 0 ? (
                    <PriceAmount>{t('pricing.freePrice')}</PriceAmount>
                  ) : (
                    <>
                      {cycle === 'yearly' && (
                        <PriceOriginal>{originalMonthlyPriceLabel}</PriceOriginal>
                      )}
                      <PriceAmount>{monthlyPriceLabel}</PriceAmount>
                      <PricePeriod>{t('pricing.perMonth')}</PricePeriod>
                    </>
                  )}
                </PriceRow>
                {cycle === 'yearly' && plan.monthlyPrice > 0 && (
                  <YearlyNote>
                    {t('pricing.yearlyTotal', { amount: yearlyTotalLabel })}
                  </YearlyNote>
                )}
                {isTrialStatusLoading && (
                  <TrialPendingNote>{t('pricing.checkingTrialEligibility')}</TrialPendingNote>
                )}
                {!isTrialStatusLoading && showProTrial && (
                  <TrialNote>{t('pricing.firstPaymentTrialNote')}</TrialNote>
                )}
                {!isTrialStatusLoading && showTrialUnavailable && (
                  <TrialDisabledNote>{t('pricing.trialUsed')}</TrialDisabledNote>
                )}
              </CardTop>

              <Divider />

              <FeatureLabel>{getFeatureLabel(plan)}</FeatureLabel>

              <FeatureList>
                {featureItems.map((f, i) => (
                  <FeatureItem key={i}>
                    <CheckIcon><Check size={14} strokeWidth={2.5} /></CheckIcon>
                    {f}
                  </FeatureItem>
                ))}
              </FeatureList>

              <Spacer />

              <CtaButton
                data-track={`pricing_select_${planKey}`}
                $recommended={!!plan.recommended}
                $isCurrent={isCurrent}
                disabled={isCurrent || loading === planKey}
                onClick={() => handleGetPlan(planKey)}
              >
                {loading === planKey
                  ? t('pricing.processing')
                  : isCurrent
                    ? t('pricing.currentPlan')
                    : showProTrial
                      ? t('pricing.startFreeTrial')
                    : planKey === 'free'
                      ? t('pricing.getFree')
                      : t('pricing.getPlan', { plan: getPlanName(planKey) })}
              </CtaButton>
            </PlanCard>
          );
        })}
      </CardsRow>
      {(showGuestTrialMarketing || proTrialAvailable === true) && (
        <TrialFootnote>{t('pricing.trialFootnote')}</TrialFootnote>
      )}

      {/* Enterprise */}
      <SectionLabel>{getPlanName('enterprise')}</SectionLabel>
      <EnterpriseRow>
        <EnterpriseCard>
          <CardHeader>
            <PlanName>{getPlanName('enterprise')}</PlanName>
          </CardHeader>

          <PriceRow>
            <PriceAmount $small>{t('pricing.customPrice')}</PriceAmount>
          </PriceRow>

          <Divider />

          <FeatureLabel>{getFeatureLabel(PLANS.enterprise)}</FeatureLabel>

          <FeatureList>
            {buildFeatureItems(PLANS.enterprise).map((f, i) => (
              <FeatureItem key={i}>
                <CheckIcon><Check size={14} strokeWidth={2.5} /></CheckIcon>
                {f}
              </FeatureItem>
            ))}
          </FeatureList>

          <Spacer />

          <CtaButton
            data-track="pricing_select_enterprise"
            $isCurrent={currentPlan === 'enterprise'}
            disabled={currentPlan === 'enterprise' || loading === 'enterprise'}
            onClick={() => handleGetPlan('enterprise')}
          >
            {loading === 'enterprise'
              ? t('pricing.processing')
              : currentPlan === 'enterprise'
                ? t('pricing.currentPlan')
                : t('pricing.contactSales')}
          </CtaButton>
        </EnterpriseCard>
      </EnterpriseRow>

      {/* Footer */}
      <PageFooter>
        <FooterLogo src="/agent_logo.png" alt="" />
        <span>TEAMREBOOTT, Inc.</span>
      </PageFooter>
    </Wrap>
  );
};

export default PricingPage;

/* ------------------------------------------------------------------ */
/* Arrow Icon                                                          */
/* ------------------------------------------------------------------ */

const ArrowLeftIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Wrap = styled.div`
  height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: ${({ theme }) => theme.colors.bgSecondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 16px 80px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const TopBar = styled.header`
  width: 100%;
  max-width: 1100px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    border-color: ${({ theme }) => theme.colors.borderInput};
  }

  svg { width: 18px; height: 18px; }
`;

const TopBarTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: -0.01em;
`;

const PageTitle = styled.h1`
  margin: 24px 0 20px;
  font-size: 2.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.03em;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.75rem;
  }
`;

/* ---- Billing Toggle ---- */

const ToggleWrap = styled.div`
  margin-bottom: 36px;
`;

const TogglePill = styled.div`
  display: inline-flex;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  overflow: hidden;
`;

const ToggleOption = styled.button<{ $active: boolean }>`
  padding: 8px 20px;
  border: none;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  border-radius: 20px;

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.colors.textPrimary};
          color: ${theme.colors.bgPrimary};
        `
      : css`
          background: transparent;
          color: ${theme.colors.textSecondary};
          &:hover { background: ${theme.colors.bgHover}; }
        `}
`;

/* ---- Section ---- */

const SectionLabel = styled.div`
  width: 100%;
  max-width: 1100px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 12px;
  padding-left: 4px;
`;

/* ---- Cards ---- */

const CardsRow = styled.div`
  width: 100%;
  max-width: 1100px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 40px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div<{ $recommended?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 28px 24px 24px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme, $recommended }) =>
    $recommended ? theme.colors.textPrimary : theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  transition: border-color ${({ theme }) => theme.transitions.fast};
  min-height: 380px;
`;

const EnterpriseRow = styled.div`
  width: 100%;
  max-width: 1100px;
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
`;

const EnterpriseCard = styled(PlanCard)`
  width: 100%;
  max-width: 100%;
`;

const CardTop = styled.div`
  min-height: 130px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const PlanName = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.01em;
`;

const RecommendedBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.brand};
  letter-spacing: 0.01em;
`;

const TrialBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.success};
  letter-spacing: 0.01em;
`;

/* ---- Price ---- */

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 4px;
`;

const PriceOriginal = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-decoration: line-through;
`;

const PriceAmount = styled.span<{ $small?: boolean }>`
  font-size: ${({ $small }) => ($small ? '1.25rem' : '1.75rem')};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.02em;
`;

const PricePeriod = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const YearlyNote = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

const TrialNote = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.success};
  font-weight: 600;
  margin-bottom: 4px;
`;

const TrialPendingNote = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

const TrialDisabledNote = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

/* ---- Features ---- */

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  margin: 16px 0;
`;

const FeatureLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  font-style: italic;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 12px;
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.4;
`;

const CheckIcon = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-top: 1px;
`;

const Spacer = styled.div`
  flex: 1;
`;

/* ---- CTA ---- */

const CtaButton = styled.button<{
  $recommended?: boolean;
  $isCurrent?: boolean;
}>`
  width: 100%;
  padding: 10px 16px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-top: 16px;

  ${({ $recommended, $isCurrent, theme }) =>
    $isCurrent
      ? css`
          background: ${theme.colors.bgTertiary};
          color: ${theme.colors.textMuted};
          border: 1px solid ${theme.colors.border};
          cursor: default;
        `
      : $recommended
        ? css`
            background: ${theme.colors.textPrimary};
            color: ${theme.colors.bgPrimary};
            border: 1px solid ${theme.colors.textPrimary};
            &:hover:not(:disabled) { opacity: 0.85; }
          `
        : css`
            background: ${theme.colors.bgPrimary};
            color: ${theme.colors.textPrimary};
            border: 1px solid ${theme.colors.border};
            &:hover:not(:disabled) {
              background: ${theme.colors.bgHover};
              border-color: ${theme.colors.borderInput};
            }
          `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TrialFootnote = styled.p`
  width: 100%;
  max-width: 1100px;
  margin: -24px 0 32px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: left;
`;

/* ---- Footer ---- */

const PageFooter = styled.footer`
  width: 100%;
  max-width: 1100px;
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  opacity: 0.5;

  span {
    font-size: 0.6875rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const FooterLogo = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
`;
